import { ITestDefinition } from '../../models/testDefinition';
import { IEnabledTest } from '../../models/teamTestConfig';
import { resolveUnitLabel, findEnabledTest } from '../utils/resolveUnitLabel';
import { TEST_DIRECTION } from '../../enums/testing';
import { BoxPlotStats } from '../hooks/useTeamAnalytics';
import styles from './TeamBoxPlot.module.scss';

interface Props {
  categoryTests: ITestDefinition[];
  boxPlotStats: BoxPlotStats[];
  /** Config team: necessaria per mostrare la label personalizzata dell'unità */
  enabledTests?: IEnabledTest[];
}

const SVG_WIDTH = 260;
const SVG_HEIGHT = 120;
const PADDING = { top: 16, bottom: 28, left: 8, right: 8 };
const BOX_HEIGHT = 36;
const BOX_Y = (SVG_HEIGHT - PADDING.top - PADDING.bottom - BOX_HEIGHT) / 2 + PADDING.top;
const WHISKER_Y = BOX_Y + BOX_HEIGHT / 2;

function mapToX(value: number, min: number, max: number): number {
  const range = max - min || 1;
  const plot_w = SVG_WIDTH - PADDING.left - PADDING.right;
  return PADDING.left + ((value - min) / range) * plot_w;
}

function buildTicks(min: number, max: number, count = 5): number[] {
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, i) => min + step * i);
}

interface BoxSVGProps {
  stats: BoxPlotStats;
  /** sez.8.2: direzione di merito del test — orienta il label "meglio" sul box plot */
  direction?: TEST_DIRECTION;
}

function BoxSVG({ stats, direction }: BoxSVGProps) {
  const domainMin = Math.min(stats.min, ...stats.outliers) * 0.97;
  const domainMax = Math.max(stats.max, ...stats.outliers) * 1.03;
  const ticks = buildTicks(domainMin, domainMax);

  const xMin = mapToX(stats.min, domainMin, domainMax);
  const xQ1 = mapToX(stats.q1, domainMin, domainMax);
  const xMed = mapToX(stats.median, domainMin, domainMax);
  const xQ3 = mapToX(stats.q3, domainMin, domainMax);
  const xMax = mapToX(stats.max, domainMin, domainMax);

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
      style={{ overflow: 'visible' }}
      aria-label="Box plot distribuzione"
    >
      {/* Tick lines + labels */}
      {ticks.map((tick, i) => {
        const x = mapToX(tick, domainMin, domainMax);
        return (
          <g key={i}>
            <line x1={x} y1={PADDING.top} x2={x} y2={SVG_HEIGHT - PADDING.bottom} stroke="#e7e7e7" strokeWidth={1} />
            <text
              x={x}
              y={SVG_HEIGHT - PADDING.bottom + 12}
              textAnchor="middle"
              fontSize={9}
              fill="#b3b3b3"
              fontFamily="Satoshi, sans-serif"
            >
              {tick % 1 === 0 ? tick : tick.toFixed(1)}
            </text>
          </g>
        );
      })}

      {/* Whisker left */}
      <line x1={xMin} y1={WHISKER_Y} x2={xQ1} y2={WHISKER_Y} stroke="#013344" strokeWidth={1.5} />
      <line x1={xMin} y1={BOX_Y + 6} x2={xMin} y2={BOX_Y + BOX_HEIGHT - 6} stroke="#013344" strokeWidth={1.5} />

      {/* Whisker right */}
      <line x1={xQ3} y1={WHISKER_Y} x2={xMax} y2={WHISKER_Y} stroke="#013344" strokeWidth={1.5} />
      <line x1={xMax} y1={BOX_Y + 6} x2={xMax} y2={BOX_Y + BOX_HEIGHT - 6} stroke="#013344" strokeWidth={1.5} />

      {/* IQR Box */}
      <rect
        x={xQ1}
        y={BOX_Y}
        width={xQ3 - xQ1}
        height={BOX_HEIGHT}
        fill="#5BC9E0"
        fillOpacity={0.3}
        stroke="#013344"
        strokeWidth={1.5}
        rx={3}
      />

      {/* Median line */}
      <line x1={xMed} y1={BOX_Y} x2={xMed} y2={BOX_Y + BOX_HEIGHT} stroke="#013344" strokeWidth={2.5} />

      {/* Outliers */}
      {stats.outliers.map((v, i) => {
        const x = mapToX(v, domainMin, domainMax);
        return (
          <circle key={i} cx={x} cy={WHISKER_Y} r={3.5} fill="#f4845f" stroke="#fff" strokeWidth={1} />
        );
      })}

      {/* Labels Q1/Med/Q3 */}
      <text x={xQ1} y={BOX_Y - 4} textAnchor="middle" fontSize={9} fill="#4d4d4d" fontFamily="Satoshi, sans-serif">
        Q1:{stats.q1 % 1 === 0 ? stats.q1 : stats.q1.toFixed(1)}
      </text>
      <text x={xMed} y={BOX_Y - 4} textAnchor="middle" fontSize={9} fill="#013344" fontWeight="bold" fontFamily="Satoshi, sans-serif">
        Med:{stats.median % 1 === 0 ? stats.median : stats.median.toFixed(1)}
      </text>
      <text x={xQ3} y={BOX_Y - 4} textAnchor="middle" fontSize={9} fill="#4d4d4d" fontFamily="Satoshi, sans-serif">
        Q3:{stats.q3 % 1 === 0 ? stats.q3 : stats.q3.toFixed(1)}
      </text>

      {/* sez.8.2: indicatore direzione di merito — "meglio →" per higher_better, "← meglio" per lower_better */}
      {direction === TEST_DIRECTION.HIGHER_BETTER && (
        <text
          x={SVG_WIDTH - PADDING.right}
          y={PADDING.top - 2}
          textAnchor="end"
          fontSize={8}
          fill="#27ae60"
          fontFamily="Satoshi, sans-serif"
        >
          meglio →
        </text>
      )}
      {direction === TEST_DIRECTION.LOWER_BETTER && (
        <text
          x={PADDING.left}
          y={PADDING.top - 2}
          textAnchor="start"
          fontSize={8}
          fill="#27ae60"
          fontFamily="Satoshi, sans-serif"
        >
          ← meglio
        </text>
      )}
    </svg>
  );
}

export default function TeamBoxPlot({ categoryTests, boxPlotStats, enabledTests }: Props) {
  if (boxPlotStats.length === 0) {
    return (
      <div className={styles.empty}>Dati insufficienti per mostrare i box plot (minimo 2 atleti con dati).</div>
    );
  }

  const statsMap = new Map<string, BoxPlotStats>(boxPlotStats.map((s) => [s.testId, s]));

  return (
    <div className={styles.grid}>
      {categoryTests.map((test) => {
        const stats = statsMap.get(test.id);
        if (!stats) return null;
        return (
          <div key={test.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <p className={styles.testName}>{test.name}</p>
              {/* Mostra label personalizzata se configurata, altrimenti unità grezza */}
              <p className={styles.testUnit}>
                {resolveUnitLabel(test.defaultUnit, findEnabledTest(test.id, enabledTests))}
              </p>
            </div>
            <div className={styles.svgWrapper}>
              {/* sez.8.2: direction passata a BoxSVG per orientare il label merito */}
              <BoxSVG stats={stats} direction={test.direction} />
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryItem}>Min <strong>{stats.min % 1 === 0 ? stats.min : stats.min.toFixed(1)}</strong></span>
              <span className={styles.summaryItem}>IQR <strong>{((stats.q3 - stats.q1) % 1 === 0 ? stats.q3 - stats.q1 : (stats.q3 - stats.q1).toFixed(1))}</strong></span>
              <span className={styles.summaryItem}>Max <strong>{stats.max % 1 === 0 ? stats.max : stats.max.toFixed(1)}</strong></span>
              {stats.outliers.length > 0 && (
                <span className={styles.outlierBadge}>{stats.outliers.length} outlier</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
